import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gauge,
  Building2,
  PlugZap,
  ShieldCheck,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  HelpCircle,
} from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import enphaseLogoWithText from "../../../assets/enphase_logo_with_text.png";
import { FeatureGate } from "../../utils/featureAccess";
import useAuth from "../../../pages/Auth/hooks/useAuth";
import { getSessionProfile, isEnphaseUser } from "../../utils/featureAccess";
import { getUserInitials } from "../../utils/utility";
import { NAV_ITEMS, SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED } from "./Sidebar.constants";
import { getLocalStorage, setLocalStorage, LOCAL_STORAGE_KEYS } from "../../utils/localStorageUtil";
import { applyTheme } from "../../utils/utility";
import "./Sidebar.scss";

const ICON_MAP = { Gauge, Building2, PlugZap, ShieldCheck };

const Sidebar = ({ collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const { displayName, email: userEmail } = getSessionProfile();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const [theme, setTheme] = useState(getLocalStorage(LOCAL_STORAGE_KEYS.THEME, false) || "light");

  const enphaseUser = isEnphaseUser();

  const initials = getUserInitials({ first_name: displayName });
  const userName = displayName;

  useEffect(() => {
    applyTheme(theme);
    setLocalStorage(LOCAL_STORAGE_KEYS.THEME, theme);
  }, [theme]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    if (isProfileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileOpen]);

  const handleLogout = () => {
    setIsProfileOpen(false);
    logout();
  };

  const toggleTheme = () => {
    setTheme(prev => (prev === "light" ? "dark" : "light"));
  };

  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  return (
    <motion.aside
      className={`sidebar ${collapsed ? "sidebar--collapsed" : ""}`}
      animate={{ width: sidebarWidth }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Brand */}
      <div className="sidebar__brand">
        <NavLink to="/" className="sidebar__brand-link" aria-label="Go to dashboard">
          <AnimatePresence mode="wait">
            {collapsed ? (
              <motion.img
                key="icon"
                src={enphaseLogoWithText}
                alt="Enphase"
                className="sidebar__brand-icon-img"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
              />
            ) : (
              <motion.img
                key="full"
                src={enphaseLogoWithText}
                alt="Enphase"
                className="sidebar__brand-logo"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
              />
            )}
          </AnimatePresence>
        </NavLink>
      </div>
      <button
        type="button"
        className="sidebar__collapse-btn"
        onClick={onToggleCollapse}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Navigation */}
      <nav className="sidebar__nav">
        <ul className="sidebar__list">
          {NAV_ITEMS.filter(item => !(enphaseUser && item.to === "/stations")).map(item => {
            const IconComponent = ICON_MAP[item.iconName];
            const isActive = location.pathname.startsWith(item.to);

            const linkContent = (
              <li key={item.to} className="sidebar__item">
                <Tooltip
                  title={item.label}
                  placement="right"
                  arrow
                  disableHoverListener={!collapsed}
                  disableFocusListener={!collapsed}
                >
                  <NavLink to={item.to} className={`sidebar__link ${isActive ? "sidebar__link--active" : ""}`}>
                    <span className="sidebar__link-icon">{IconComponent && <IconComponent size={20} />}</span>
                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span
                          className="sidebar__link-label"
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {isActive && (
                      <motion.div
                        className="sidebar__active-indicator"
                        layoutId="sidebar-active"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </NavLink>
                </Tooltip>
              </li>
            );

            if (item.featureId) {
              return (
                <FeatureGate key={item.to} featureId={item.featureId}>
                  {linkContent}
                </FeatureGate>
              );
            }
            return linkContent;
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="sidebar__footer">
        {/* Theme Toggle */}
        <Tooltip
          title={theme === "light" ? "Dark Mode" : "Light Mode"}
          placement="right"
          arrow
          disableHoverListener={!collapsed}
          disableFocusListener={!collapsed}
        >
          <button type="button" className="sidebar__theme-toggle" onClick={toggleTheme}>
            <span className="sidebar__link-icon">{theme === "light" ? <Moon size={18} /> : <Sun size={18} />}</span>
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  className="sidebar__link-label"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {theme === "light" ? "Dark Mode" : "Light Mode"}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </Tooltip>

        {/* Help / Support */}
        <Tooltip
          title="Help & Support"
          placement="right"
          arrow
          disableHoverListener={!collapsed}
          disableFocusListener={!collapsed}
        >
          <a
            href="https://support.enphase.com"
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar__footer-btn"
          >
            <span className="sidebar__link-icon">
              <HelpCircle size={18} />
            </span>
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  className="sidebar__link-label"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  Help &amp; Support
                </motion.span>
              )}
            </AnimatePresence>
          </a>
        </Tooltip>

        {/* Profile / Email — click to show logout */}
        <div className="sidebar__profile" ref={profileRef}>
          <Tooltip
            title={collapsed ? (userEmail ? `${userName} — ${userEmail}` : userName) : userEmail || userName}
            placement="right"
            arrow
          >
            <div
              className="sidebar__profile-btn"
              role="button"
              tabIndex={0}
              onClick={() => setIsProfileOpen(prev => !prev)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsProfileOpen(prev => !prev);
                }
              }}
            >
              <span className="sidebar__avatar">{initials}</span>
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    className="sidebar__profile-info"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="sidebar__profile-name">{userName}</span>
                    <span className="sidebar__profile-email">{userEmail}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Tooltip>

          {/* Logout Dropdown */}
          <AnimatePresence>
            {isProfileOpen && (
              <motion.div
                className="sidebar__profile-dropdown"
                initial={{ opacity: 0, y: 6, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                <div className="sidebar__profile-dropdown-header">
                  <span className="sidebar__profile-dropdown-avatar">{initials}</span>
                  <div className="sidebar__profile-dropdown-info">
                    <span className="sidebar__profile-dropdown-name">{userName}</span>
                    <span className="sidebar__profile-dropdown-email">{userEmail}</span>
                  </div>
                </div>
                <div className="sidebar__profile-dropdown-divider" />
                <button type="button" className="sidebar__logout-btn" onClick={handleLogout}>
                  <LogOut size={16} /> Logout
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.aside>
  );
};

Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
};

export default Sidebar;
